import React, { useRef, useState } from 'react';
import {
  View,
  Image,
  FlatList,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { A, H2, P } from '@expo/html-elements';
import { LinearGradient } from 'expo-linear-gradient';
import ReactPlayer from 'react-player/lazy';

import {
  CreateResponsiveStyle,
  isWeb,
  maxWidthContainer,
} from '~global-screens';
import { Fonts, Colors, vs, ms, hs } from '@web-styles';
import { capitalizeLetter } from '@web-utils';

import { setShortListData } from '~store/section/sectionSlice';

const SEE_MORE_SHORTS = require('~assets/images/see-more-shorts.png');

const TagsiteBeautyShortsOverlay = ({ beautyShorts, backgroundImage }) => {
  const dispatch = useDispatch();
  const styles = componentStyles();
  const listRef = useRef(FlatList);
  const [hoveredVideoId, setHoveredVideoId] = useState(null);
  const [readyVideoId, setReadyVideoId] = useState(null);

  // Handle click on beauty shorts to set data for the beauty shorts page
  const handleShortClick = (event, short) => {
    if (short) {
      event.preventDefault();
      dispatch(setShortListData(short));
      window.location.href = `/beauty-short/${short.slug}`;
    }
  };

  const handleVideoLeave = () => {
    setHoveredVideoId(null);
    setReadyVideoId(null);
  };

  const renderItem = ({ item, index }) => {
    const marginLeft = {
      marginLeft: index === 0 ? (isWeb ? 64 : hs(16)) : 0,
    };

    const isHovered = isWeb && hoveredVideoId === item?._id;
    const isVideoReady = isWeb && readyVideoId === item?._id;
    const isLoading = isHovered && !isVideoReady;
    const videoUrl = item?.attachments?.[0]?.url;

    // Only apply hover events on web
    const hoverProps = isWeb
      ? {
          onMouseEnter: () => setHoveredVideoId(item?._id),
          onMouseLeave: handleVideoLeave,
        }
      : {};

    return (
      <View style={[marginLeft, styles('itemContainer')]}>
        <Pressable
          style={styles('videoScreenContainer')}
          onPress={(e) => handleShortClick(e, item)}
          {...hoverProps}>
          <View
            style={[
              styles('thumbnailContainer'),
              isVideoReady && styles('hideThumb'),
            ]}>
            <Image
              source={{
                uri: `https://img.youtube.com/vi/${item?.attachments?.[0]?.url}/maxresdefault.jpg`,
              }}
              style={styles('thumbnailShort')}
              resizeMode="cover"
            />
          </View>

          {isLoading && (
            <View style={styles('loaderContainer')}>
              <ActivityIndicator size="large" color={Colors.WHITE} />
            </View>
          )}

          {isHovered && videoUrl && (
            <View style={styles('videoPlayerContainer')}>
              <ReactPlayer
                url={`https://www.youtube.com/watch?v=${videoUrl}`}
                playing={true}
                controls={false}
                muted={true}
                width="100%"
                height="100%"
                style={styles('reactPlayer')}
                onReady={() => setReadyVideoId(item?._id)}
                config={{
                  youtube: {
                    playerVars: {
                      autoplay: 1,
                      controls: 0,
                      showinfo: 0,
                      rel: 0,
                      iv_load_policy: 3,
                      modestbranding: 1,
                      start: 15,
                    },
                  },
                }}
              />
            </View>
          )}

          {!isVideoReady && !isLoading && (
            <View style={styles('playContainer')}>
              <Image
                source={require('~assets/images/play-short.svg')}
                style={styles('playIcon')}
              />
            </View>
          )}

          {/* Gradient Overlay for Title */}
          <LinearGradient
            colors={['transparent', 'rgba(0,0,0,1)']}
            style={styles('gradientOverlay')}
          />

          {/* Category and Title overlaid on image */}
          <View style={styles('textOverlay')}>
            <P
              numberOfLines={1}
              style={[Fonts('categoryMeta'), styles('categoryText')]}>
              <A href={`/search/${item?.category?.slug}`}>
                {capitalizeLetter(item?.category?.name)}
              </A>
            </P>
            <H2 numberOfLines={2} style={styles('titleText')}>
              <A href={`/beauty-short/${item.slug}`}>{item?.title}</A>
            </H2>
          </View>
        </Pressable>
      </View>
    );
  };

  // If no beauty shorts or background image, don't render anything
  if (!beautyShorts?.length || !backgroundImage) {
    return null;
  }

  return (
    <View style={styles('container')}>
      {/* Background Image */}
      <Image
        source={{ uri: backgroundImage }}
        style={styles('backgroundImage')}
        resizeMode="cover"
      />

      {/* Beauty Shorts List */}
      <View
        style={styles('listContainer')}
        nativeID="tagsite-beauty-shorts-list">
        <FlatList
          ref={listRef}
          data={beautyShorts}
          keyExtractor={(item, index) =>
            `tagsite-beauty-short-${item?._id}-${index}`
          }
          renderItem={renderItem}
          horizontal
          showsHorizontalScrollIndicator={false}
        />
      </View>

      <View style={styles('containerSeeMore')}>
        <A
          href="/beauty-short"
          style={[Fonts('submitBtnQuiz'), styles('textDecoration')]}>
          SEE MORE
        </A>
        <Pressable
          onPress={() =>
            listRef?.current?.scrollToOffset({ offset: 300, animated: true })
          }>
          <Image source={SEE_MORE_SHORTS} style={styles('seeMoreIcon')} />
        </Pressable>
      </View>
    </View>
  );
};

const webStyles = {
  container: {
    width: '100vw',
    height: 723.25,
    position: 'relative',
    paddingVertical: 60,
    marginBottom: 30,
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
  },
  listContainer: {
    width: '100%',
    alignSelf: 'center',
    paddingTop: 100,
    position: 'relative',
  },
  itemContainer: {
    width: 268,
    height: 480,
    marginRight: 20,
    borderRadius: 18,
    overflow: 'hidden', // Ensures the rounded corners work with the image
  },
  videoScreenContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  },
  thumbnailContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 18,
  },
  thumbnailShort: {
    width: '100%',
    height: '100%',
    transform: [],
    borderRadius: 18,
    objectFit: 'cover',
    objectPosition: 'center',
  },
  playContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
    zIndex: 2,
  },
  playIcon: {
    width: 50,
    height: 72,
  },
  gradientOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 180,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    zIndex: 1,
  },
  textOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 20,
    zIndex: 2,
  },
  categoryText: {
    color: Colors.WHITE,
    marginBottom: 8,
    fontWeight: '500',
  },
  titleText: {
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 18,
    lineHeight: '22px',
    color: Colors.WHITE,
    marginBottom: 10,
    fontWeight: '600',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
  },
  containerSeeMore: {
    flexDirection: 'row',
    columnGap: 20,
    alignItems: 'center',
    alignSelf: 'center',
    width: '100%',
    maxWidth: maxWidthContainer,
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: 20,
    marginTop: 20,
  },
  textDecoration: {
    textDecorationLine: 'underline',
    color: Colors.WHITE,
  },
  seeMoreIcon: {
    width: 34,
    height: 34,
  },
  reactPlayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    borderRadius: 18,
    overflow: 'hidden',
  },
  videoPlayerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 3,
  },
  hideThumb: {
    opacity: 0,
  },
  loaderContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
    zIndex: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 50,
    padding: 15,
  },
};

const mobileStyles = {
  container: {
    width: '100vw',
    height: 'auto',
    paddingVertical: vs(30),
    minHeight: 450,
    left: '50%',
    right: '50%',
    marginLeft: '-50vw',
    marginRight: '-50vw',
    marginBottom: 0,
  },
  titleContainer: {
    marginBottom: vs(20),
  },
  title: {
    fontSize: 24,
  },
  itemContainer: {
    width: hs(250),
    height: 420,
    marginRight: Math.min(hs(12), 20),
    borderRadius: ms(14),
  },
  thumbnailContainer: {
    borderRadius: ms(14),
  },
  thumbnailShort: {
    transform: [],
    height: '100%',
  },
  gradientOverlay: {
    height: 150,
    borderBottomLeftRadius: ms(14),
    borderBottomRightRadius: ms(14),
  },
  textOverlay: {
    padding: ms(14),
  },
  titleText: {
    fontSize: 14,
    lineHeight: '18px',
  },
  categoryText: {
    fontSize: 10,
  },
  containerSeeMore: {
    width: '100%',
    alignSelf: 'center',
    paddingTop: vs(20),
    paddingRight: hs(10),
    marginTop: vs(15),
    flexDirection: 'row',
    columnGap: vs(10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  playContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
    zIndex: 2,
  },
  playIcon: {
    width: 45,
    height: 65,
    resizeMode: 'contain',
  },
  listContainer: {
    paddingTop: vs(80),
  },
  reactPlayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    borderRadius: ms(14),
    overflow: 'hidden',
  },
  videoPlayerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 3,
  },
  hideThumb: {
    opacity: 0,
  },
  loaderContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
    zIndex: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 40,
    padding: ms(12),
  },
};

const componentStyles = CreateResponsiveStyle(webStyles, mobileStyles);

TagsiteBeautyShortsOverlay.propTypes = {
  beautyShorts: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      attachments: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
        }),
      ),
      category: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
      }),
      title: PropTypes.string,
      slug: PropTypes.string,
    }),
  ),
  backgroundImage: PropTypes.string,
};

export default TagsiteBeautyShortsOverlay;
